import React, { useState, useCallback } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';

interface AdditionObject {
  id: string;
  emoji: string;
  name: string;
  color: string;
}

interface AdditionProblem {
  id: string;
  question: string;
  leftGroup: number;
  rightGroup: number;
  answer: number;
}

interface AdditionManipulatorConfigurationFormProps {
  config: any;
  onChange: (config: any) => void;
}

export const AdditionManipulatorConfigurationForm: React.FC<AdditionManipulatorConfigurationFormProps> = ({
  config,
  onChange
}) => {
  const [objects, setObjects] = useState<AdditionObject[]>(config.objects || []);
  const [problems, setProblems] = useState<AdditionProblem[]>(config.problems || []);

  const addObject = useCallback(() => {
    const newObject: AdditionObject = {
      id: `obj_${Date.now()}`,
      emoji: '🍎',
      name: 'Nouvel objet',
      color: 'red'
    };
    const newObjects = [...objects, newObject];
    setObjects(newObjects);
    onChange({ ...config, objects: newObjects });
  }, [objects, config, onChange]);

  const updateObject = useCallback((index: number, field: keyof AdditionObject, value: string) => {
    const newObjects = objects.map((obj, i) => 
      i === index ? { ...obj, [field]: value } : obj
    );
    setObjects(newObjects);
    onChange({ ...config, objects: newObjects });
  }, [objects, config, onChange]);

  const removeObject = useCallback((index: number) => {
    const newObjects = objects.filter((_, i) => i !== index);
    setObjects(newObjects);
    onChange({ ...config, objects: newObjects });
  }, [objects, config, onChange]);

  const addProblem = useCallback(() => {
    const newProblem: AdditionProblem = {
      id: `p_${Date.now()}`,
      question: 'Combien y a-t-il d\'objets en tout ?',
      leftGroup: 2,
      rightGroup: 3,
      answer: 5
    };
    const newProblems = [...problems, newProblem];
    setProblems(newProblems);
    onChange({ ...config, problems: newProblems });
  }, [problems, config, onChange]);

  const updateProblem = useCallback((index: number, field: keyof AdditionProblem, value: string | number) => {
    const newProblems = problems.map((problem, i) => 
      i === index ? { ...problem, [field]: value } : problem
    );
    setProblems(newProblems);
    onChange({ ...config, problems: newProblems });
  }, [problems, config, onChange]);

  const removeProblem = useCallback((index: number) => {
    const newProblems = problems.filter((_, i) => i !== index);
    setProblems(newProblems);
    onChange({ ...config, problems: newProblems });
  }, [problems, config, onChange]);

  const colorOptions = [
    { value: 'red', label: 'Rouge', emoji: '🔴' },
    { value: 'yellow', label: 'Jaune', emoji: '🟡' },
    { value: 'orange', label: 'Orange', emoji: '🟠' },
    { value: 'purple', label: 'Violet', emoji: '🟣' },
    { value: 'blue', label: 'Bleu', emoji: '🔵' },
    { value: 'green', label: 'Vert', emoji: '🟢' }
  ];

  const emojiOptions = [
    '🍎', '🍌', '🍊', '🍇', '🍓', '🥕', '🥔', '🍞', '🧀', '🥚',
    '🐱', '🐶', '🐰', '🐸', '🐯', '🦁', '🐻', '🐼', '🐨', '🐵',
    '⭐', '🌟', '💫', '✨', '🎈', '🎁', '🎀', '🎂', '🍰', '🧁'
  ];

  return (
    <div className="space-y-6">
      {/* Configuration de base */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de la ressource
          </label>
          <input
            type="text"
            value={config.name || ''}
            onChange={(e) => onChange({ ...config, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Manipulateur d'Additions"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre maximum
          </label>
          <input
            type="number"
            value={config.maxNumber || 10}
            onChange={(e) => onChange({ ...config, maxNumber: parseInt(e.target.value) || 10 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
            max="20"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Instructions
        </label>
        <textarea
          value={config.instructions || ''}
          onChange={(e) => onChange({ ...config, instructions: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Glissez les objets dans les groupes pour résoudre les additions"
        />
      </div>

      {/* Options */}
      <div className="flex space-x-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.showAnswer || false}
            onChange={(e) => onChange({ ...config, showAnswer: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">Afficher la réponse</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.allowDrag || false}
            onChange={(e) => onChange({ ...config, allowDrag: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">Permettre le glisser-déposer</span>
        </label>
      </div>

      {/* Objets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Objets</h3>
          <button
            onClick={addObject}
            className="flex items-center space-x-2 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un objet</span>
          </button>
        </div>
        
        <div className="space-y-3">
          {objects.map((obj, index) => (
            <div key={obj.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
              <GripVertical className="w-4 h-4 text-gray-400" />
              
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Emoji</label>
                  <select
                    value={obj.emoji}
                    onChange={(e) => updateObject(index, 'emoji', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-lg"
                  >
                    {emojiOptions.map(emoji => (
                      <option key={emoji} value={emoji}>{emoji}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
                  <input
                    type="text"
                    value={obj.name}
                    onChange={(e) => updateObject(index, 'name', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                    placeholder="Nom de l'objet"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Couleur</label>
                  <select
                    value={obj.color}
                    onChange={(e) => updateObject(index, 'color', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  >
                    {colorOptions.map(color => (
                      <option key={color.value} value={color.value}>
                        {color.emoji} {color.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={() => removeObject(index)}
                    className="w-full px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Problèmes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Problèmes d'addition</h3>
          <button
            onClick={addProblem}
            className="flex items-center space-x-2 px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un problème</span>
          </button>
        </div>
        
        <div className="space-y-3">
          {problems.map((problem, index) => (
            <div key={problem.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Problème {index + 1}</h4>
                <button
                  onClick={() => removeProblem(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Question</label>
                  <input
                    type="text"
                    value={problem.question}
                    onChange={(e) => updateProblem(index, 'question', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                    placeholder="Combien y a-t-il d'objets en tout ?"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Groupe gauche</label>
                    <input
                      type="number"
                      value={problem.leftGroup}
                      onChange={(e) => updateProblem(index, 'leftGroup', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      min="0"
                      max="10"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Groupe droit</label>
                    <input
                      type="number"
                      value={problem.rightGroup}
                      onChange={(e) => updateProblem(index, 'rightGroup', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      min="0"
                      max="10"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Réponse</label>
                    <input
                      type="number"
                      value={problem.answer}
                      onChange={(e) => updateProblem(index, 'answer', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                      min="0"
                      max="20"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdditionManipulatorConfigurationForm;
